import { ReactNode, useEffect, useMemo, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import Avatar, { AvatarProps } from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Popper from '@mui/material/Popper';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';

// third party
import PopupState, { bindPopper, bindToggle } from 'material-ui-popup-state';
import { useDispatch, useSelector } from 'react-redux';

// project imports
import Transitions from 'ui-component/extended/Transitions';
import RechargeWallet from 'RechargeWallet';
import RechargeWalletRazorpay from 'RechargeWalletRazorpay';
import useClientConfig from 'hooks/useClientConfig';
import { getDefaultPaymentGateway } from 'utils/clientConfig';
import service from 'utils/service';
import { reduxSliceData } from 'store/slices/reduxData';

// assets
import { IconAdjustmentsHorizontal, IconRefresh, IconSearch, IconWallet, IconX } from '@tabler/icons-react';

interface HeaderAvatarProps extends AvatarProps {
  children: ReactNode;
}

function HeaderAvatar({ children, ref, ...others }: HeaderAvatarProps) {
  const theme = useTheme();

  return (
    <Avatar
      ref={ref}
      variant="rounded"
      sx={{
        ...theme.typography.commonAvatar,
        ...theme.typography.mediumAvatar,
        color: theme.vars.palette.secondary.dark,
        background: theme.vars.palette.secondary.light,
        '&:hover': {
          color: theme.vars.palette.secondary.light,
          background: theme.vars.palette.secondary.dark
        },

        ...theme.applyStyles('dark', {
          color: theme.vars.palette.secondary.main,
          background: theme.vars.palette.dark.main,
          '&:hover': {
            color: theme.vars.palette.secondary.light,
            background: theme.vars.palette.secondary.main
          }
        })
      }}
      {...others}
    >
      {children}
    </Avatar>
  );
}

interface Props {
  value: string;
  setValue: (value: string) => void;
  popupState: any;
}

function getWalletRows(walletData: any): Array<{ BALANCE?: number | string }> {
  if (Array.isArray(walletData?.data)) return walletData.data;
  if (Array.isArray(walletData?.apiData)) return walletData.apiData;
  if (Array.isArray(walletData)) return walletData;
  return [];
}

function getWalletBalance(walletData: any): number {
  const rows = getWalletRows(walletData);
  return rows.reduce((total, item) => total + Number(item?.BALANCE || 0), 0);
}

// ==============================|| SEARCH INPUT - MOBILE||============================== //

function MobileSearch({ value, setValue, popupState }: Props) {
  const theme = useTheme();

  return (
    <OutlinedInput
      id="input-search-header"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      placeholder="Search"
      startAdornment={
        <InputAdornment position="start">
          <IconSearch stroke={1.5} size="16px" />
        </InputAdornment>
      }
      endAdornment={
        <InputAdornment position="end">
          <HeaderAvatar>
            <IconAdjustmentsHorizontal stroke={1.5} size="20px" />
          </HeaderAvatar>
          <Box sx={{ ml: 2 }}>
            <Avatar
              variant="rounded"
              sx={{
                ...theme.typography.commonAvatar,
                ...theme.typography.mediumAvatar,
                bgcolor: 'orange.light',
                color: 'orange.dark',
                '&:hover': { bgcolor: 'orange.dark', color: 'orange.light' },

                ...theme.applyStyles('dark', { bgcolor: theme.vars.palette.dark.main })
              }}
              {...bindToggle(popupState)}
            >
              <IconX stroke={1.5} size="20px" />
            </Avatar>
          </Box>
        </InputAdornment>
      }
      aria-describedby="search-helper-text"
      slotProps={{ input: { 'aria-label': 'weight', sx: { bgcolor: 'transparent', pl: 0.5 } } }}
      sx={{ width: '100%', ml: 0.5, px: 2, bgcolor: 'background.paper' }}
    />
  );
}
function MobileRechargeButton({ value, setValue, popupState }: Props) {
  const theme = useTheme();

  return (
            <Avatar
              variant="rounded"
              sx={{
                ...theme.typography.commonAvatar,
                ...theme.typography.mediumAvatar,
                bgcolor: 'orange.light',
                color: 'orange.dark',
                '&:hover': { bgcolor: 'orange.dark', color: 'orange.light' },

                ...theme.applyStyles('dark', { bgcolor: theme.vars.palette.dark.main })
              }}
              {...bindToggle(popupState)}
            >
              <IconX stroke={1.5} size="20px" />
            </Avatar>
  );
}

// ==============================|| SEARCH INPUT ||============================== //

export default function SearchSection() {
  const [value, setValue] = useState('');
  const [walletOpen, setWalletOpen] = useState(false);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [paymentGateway, setPaymentGateway] = useState(getDefaultPaymentGateway());
  const clientConfig = useClientConfig();
  const dispatch = useDispatch();
  const reduxBag = useSelector((state: any) => state.reduxData?.reduxData ?? {});
  const getWalletData = reduxBag?.getWalletData;
  const paymentSuccess = reduxBag?.paymentSuccess;

  const loginCustomer = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('loginCustomer') || '[]');
    } catch {
      return [];
    }
  }, []);

  const customerId = loginCustomer?.[0]?.UserData?.[0]?.CUSTOMER_ID;

  useEffect(() => {
    setWalletBalance(getWalletBalance(getWalletData));
  }, [getWalletData]);

  useEffect(() => {
    if (clientConfig?.paymentGateway) {
      setPaymentGateway(String(clientConfig.paymentGateway).toLowerCase());
    }
  }, [clientConfig]);

  useEffect(() => {
    void fetchWalletData();
  }, [customerId, paymentSuccess]);

  const fetchWalletData = async () => {
    if (!customerId) return;

    const walletMaster = (service as any)?.API_URL?.wallet_master;
    if (!walletMaster?.walletData) return;

    try {
      setWalletLoading(true);
      const response: any = await service.makeAPICall({
        methodName: service.Methods.POST,
        apiUrl: walletMaster.walletData,
        body: { CUSTOMER_ID: [customerId] },
      });

      if (response?.apiStatus || response?.code === 200 || response?.success === true) {
        const payload = response?.apiData ?? response?.data ?? response;
        dispatch(reduxSliceData({ key: 'getWalletData', data: payload }));
        setWalletBalance(getWalletBalance(payload));
      }
    } finally {
      setWalletLoading(false);
    }
  };

  const walletSummary = (
    <Chip
      icon={<IconWallet stroke={1.5} size="16px" />}
      label={walletLoading ? 'Loading...' : `₹${walletBalance.toFixed(2)}`}
      variant="outlined"
      sx={{
        height: 40,
        borderRadius: 2,
        px: 0.5,
        '& .MuiChip-icon': { ml: 1.25 },
        '& .MuiChip-label': { pr: 1.5, fontWeight: 600 }
      }}
    />
  );

  return (
    <>
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <PopupState variant="popper" popupId="demo-popup-popper">
          {(popupState) => (
            <>
              <Box sx={{ ml: 2 }}>
                <HeaderAvatar {...bindToggle(popupState)}>
                  <IconWallet stroke={1.5} size="19.2px" />
                </HeaderAvatar>
              </Box>
              <Popper
                {...bindPopper(popupState)}
                transition
                sx={{ zIndex: 1100, width: '99%', top: '-55px !important', px: { xs: 1.25, sm: 1.5 } }}
              >
                {({ TransitionProps }) => (
                  <>
                    <Transitions type="zoom" {...TransitionProps} sx={{ transformOrigin: 'center left' }}>
                      <Card sx={{ bgcolor: 'background.default', border: 0, boxShadow: 'none' }}>
                        <Box sx={{ p: 2 }}>
                          <Stack  spacing={2}>

                            <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap">
                               {walletSummary}
                              <Tooltip title="Refresh wallet balance">
                                <IconButton onClick={() => void fetchWalletData()} disabled={walletLoading} size="small">
                                  {walletLoading ? <CircularProgress size={18} /> : <IconRefresh stroke={1.5} size="18px" />}
                                </IconButton>
                              </Tooltip>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => setWalletOpen(true)}
                                sx={{ borderRadius: 2 }}
                              >
                                Recharge Wallet
                              </Button>
                              
                              <Grid container sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                <Grid size="grow">
                                  <MobileRechargeButton value={value} setValue={setValue} popupState={popupState} />
                               </Grid>
                              </Grid>

                            </Stack>
                          </Stack>
                        </Box>
                      </Card>
                    </Transitions>
                  </>
                )}
              </Popper>
            </>
          )}
        </PopupState>
      </Box>
      <Box >
        <Stack sx={{ display: { xs: 'none', md: 'block' } }} direction="row" alignItems="center" spacing={1.5} >
          {/* <OutlinedInput
            id="input-search-header"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Search"
            startAdornment={
              <InputAdornment position="start">
                <IconSearch stroke={1.5} size="16px" />
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                <HeaderAvatar>
                  <IconAdjustmentsHorizontal stroke={1.5} size="20px" />
                </HeaderAvatar>
              </InputAdornment>
            }
            aria-describedby="search-helper-text"
            slotProps={{ input: { 'aria-label': 'weight', sx: { bgcolor: 'transparent', pl: 0.5 } } }}
            sx={{ width: { md: 250, lg: 434 }, px: 2 }}
          /> */}

          {walletSummary}

          <Tooltip title="Refresh wallet balance">
            <IconButton onClick={() => void fetchWalletData()} disabled={walletLoading} size="small">
              {walletLoading ? <CircularProgress size={18} /> : <IconRefresh stroke={1.5} size="18px" />}
            </IconButton>
          </Tooltip>

          <Button variant="contained" onClick={() => setWalletOpen(true)} sx={{ borderRadius: 2, height: 40 }}>
            Recharge Wallet
          </Button>
        </Stack>
      </Box>

      {paymentGateway === 'razorpay' ? (
        <RechargeWalletRazorpay visible={walletOpen} onClose={() => setWalletOpen(false)} />
      ) : (
        <RechargeWallet visible={walletOpen} onClose={() => setWalletOpen(false)} />
      )}
    </>
  );
}
